import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["loadOptions"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { memo, useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { createUseStyles, FieldGroup, Select } from '@Sage/hoi-poi-ui';
import { useWeb5Context } from "../../../../../../../../lib/Web5Context";
import { useDebounce } from "../../../../../../../../hooks";
import { getRelatedEntities, getRelatedEntitiesReverse } from "../../../../config";
import styles from "./styles";
const useStyles = createUseStyles(styles, 'SelectWithEntity');

// Specific field for XAxis and xAxisBreakdown
const SelectorWithEntity = /*#__PURE__*/memo(_ref => {
  let {
    id,
    onChange,
    data,
    label,
    subFields,
    isRequired,
    error
  } = _ref;
  const classes = useStyles();
  const {
    t
  } = useWeb5Context();
  const isFirstRender = useRef(true);
  const prevEntity = useRef(null);
  const [options, setOptions] = useState([]);
  const [filteredEntities, setFilteredEntities] = useState([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const entities = useMemo(() => {
    return subFields[0].loadCustomOptions({
      t
    });
  }, [t, subFields]);
  const mappedEntities = useMemo(() => {
    return entities.reduce((acc, entity) => {
      acc[entity.value] = entity;
      return acc;
    }, {});
  }, [entities]);
  const getEntityNameFromValue = useCallback(value => {
    var _value$split, _value$split$call;
    return (value === null || value === void 0 ? void 0 : (_value$split = value.split) === null || _value$split === void 0 ? void 0 : (_value$split$call = _value$split.call(value, '.')) === null || _value$split$call === void 0 ? void 0 : _value$split$call[0]) || '';
  }, []);
  const getEntityName = useCallback(options => {
    var _options$;
    let entityName = getEntityNameFromValue(options === null || options === void 0 ? void 0 : (_options$ = options[0]) === null || _options$ === void 0 ? void 0 : _options$.value);
    const relatedEntitiesReverse = getRelatedEntitiesReverse();
    if (relatedEntitiesReverse[entityName]) {
      return relatedEntitiesReverse[entityName];
    } else return entityName;
  }, [getEntityNameFromValue]);

  // Define the entityOptions based on the entities existing in the second selector
  const getFilteredEntities = useCallback(options => {
    if (!(entities !== null && entities !== void 0 && entities.length)) return [];
    let newFilteredEntities = [];
    options.forEach(option => {
      const entityName = getEntityName(option.options);
      newFilteredEntities.push(mappedEntities[entityName]);
    });
    setFilteredEntities(newFilteredEntities);
    return newFilteredEntities;
  }, [entities, mappedEntities, getEntityName]);
  const fetchOptions = useCallback(parentValue => {
    setIsLoadingOptions(true);
    subFields[1].loadOptions(parentValue)().then(newOptions => {
      setOptions(newOptions);
      getFilteredEntities(newOptions);
    }).finally(() => setIsLoadingOptions(false));
  }, [subFields, getFilteredEntities]);
  const debouncedFetchOptions = useDebounce(fetchOptions, 300);
  useEffect(() => {
    var _data$entity;
    if (isFirstRender.current && data !== null && data !== void 0 && (_data$entity = data.entity) !== null && _data$entity !== void 0 && _data$entity.value) {
      var _data$entity2;
      // Get Options for both selectors in the first render
      isFirstRender.current = false;
      if (data !== null && data !== void 0 && (_data$entity2 = data.entity) !== null && _data$entity2 !== void 0 && _data$entity2.value) {
        const parentValue = data[subFields[1].parentId].value.toLowerCase();
        debouncedFetchOptions(parentValue);
      }
    } else {
      var _data$entity3, _data$entity4, _data$entity6;
      // Get options and entity in any other render
      if (prevEntity.current !== (data === null || data === void 0 ? void 0 : (_data$entity3 = data.entity) === null || _data$entity3 === void 0 ? void 0 : _data$entity3.value) && data !== null && data !== void 0 && (_data$entity4 = data.entity) !== null && _data$entity4 !== void 0 && _data$entity4.value) {
        var _data$entity5;
        setOptions([]);
        setFilteredEntities([]);
        prevEntity.current = data === null || data === void 0 ? void 0 : (_data$entity5 = data.entity) === null || _data$entity5 === void 0 ? void 0 : _data$entity5.value;
        const parentValue = data[subFields[1].parentId].value.toLowerCase();
        debouncedFetchOptions(parentValue);
      } else if (!(data !== null && data !== void 0 && (_data$entity6 = data.entity) !== null && _data$entity6 !== void 0 && _data$entity6.value)) {
        setOptions([]);
        setFilteredEntities([]);
      }
    }
  }, [data, debouncedFetchOptions, subFields]);
  const filteredOptions = useMemo(() => {
    var _entity$value, _entity$value$toLower;
    const entity = data === null || data === void 0 ? void 0 : data[subFields[0].id];
    if (!(entity !== null && entity !== void 0 && entity.value)) return [];
    const entityValue = entity === null || entity === void 0 ? void 0 : (_entity$value = entity.value) === null || _entity$value === void 0 ? void 0 : (_entity$value$toLower = _entity$value.toLowerCase) === null || _entity$value$toLower === void 0 ? void 0 : _entity$value$toLower.call(_entity$value);
    const relatedEntities = getRelatedEntities();
    return options.reduce((arr, current) => {
      current.options.forEach(option => {
        var _relatedEntities$enti;
        const optionEntityValue = option.value.toLowerCase().split('.')[0];
        if (optionEntityValue === entityValue || (_relatedEntities$enti = relatedEntities[entityValue]) !== null && _relatedEntities$enti !== void 0 && _relatedEntities$enti.includes(optionEntityValue)) {
          arr.push(option);
        }
      });
      return arr;
    }, []);
  }, [options, subFields, data]);
  const propsWithoutLoadOptions = useMemo(() => {
    const _subFields$ = subFields[1],
      {
        loadOptions
      } = _subFields$,
      rest = _objectWithoutProperties(_subFields$, _excluded);
    return rest;
  }, [subFields]);
  const inputProps = useMemo(() => {
    return [_objectSpread(_objectSpread({
      isFullWidth: true
    }, subFields[0]), {}, {
      onChange: onChange({
        fieldId: subFields[0].id
      }),
      value: data === null || data === void 0 ? void 0 : data[subFields[0].id],
      options: filteredEntities,
      isLoading: isLoadingOptions,
      isReadOnly: subFields[0].getIsReadOnly({
        fieldId: subFields[0].id,
        data
      })
    }), _objectSpread(_objectSpread({
      isFullWidth: true
    }, propsWithoutLoadOptions), {}, {
      value: data === null || data === void 0 ? void 0 : data[subFields[1].id],
      onChange: onChange({
        fieldId: subFields[1].id
      }),
      options: filteredOptions,
      isReadOnly: subFields[1].getIsReadOnly({
        fieldId: subFields[1].id,
        data
      }),
      isLoading: isLoadingOptions
    })];
  }, [filteredEntities, isLoadingOptions, propsWithoutLoadOptions, subFields, data, onChange, filteredOptions]);
  return /*#__PURE__*/React.createElement("div", {
    className: classes.root
  }, /*#__PURE__*/React.createElement(FieldGroup, {
    label: label,
    labelMode: "vertical",
    fieldsMode: "vertical",
    inputs: [Select, Select],
    isFullWidth: true,
    isRequired: isRequired,
    error: error,
    inputProps: inputProps
  }));
});
export default SelectorWithEntity;